import React, { useState, useEffect } from 'react';
import ComplianceBadges from '../components/ComplianceBadges';

interface LayerDetail { layer_id: number; layer_name: string; status: string; evidence: string[]; gaps: string[]; owner: string; }
interface SummaryModel { model_id: string; model_name: string; tier: number; overall_status: string; layer_statuses: number[]; }
interface Summary { total_models: number; compliant: number; conditional: number; non_compliant: number; models: SummaryModel[]; }
interface ModelDetail { model_id: string; model_name: string; overall_status: string; validation_hash: string; layers: LayerDetail[]; risk_classification: { tier: number; override_required: boolean }; context_of_use: { business_purpose: string; regulatory_basis: string[] }; }

const LAYER_NAMES = ['COU', 'Risk', 'Data', 'Dev', 'Perf', 'Human', 'Audit', 'Life'];

const card: React.CSSProperties = { backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: '16px 20px' };
const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 };

function statusDot(score: number) {
  const color = score === 1 ? '#48bb78' : score === 0.5 ? '#ecc94b' : '#f56565';
  return <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />;
}

function statusBadge(status: string) {
  const map: Record<string, { color: string; bg: string }> = {
    compliant: { color: '#48bb78', bg: '#22543d' },
    conditional: { color: '#ecc94b', bg: '#744210' },
    'non-compliant': { color: '#f56565', bg: '#742a2a' },
  };
  const s = map[status] || map['conditional'];
  return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, color: s.color, backgroundColor: s.bg }}>{status}</span>;
}

function tierBadge(tier: number) {
  const colors = { 1: '#63b3ed', 2: '#ecc94b', 3: '#f56565' };
  return <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, color: '#fff', backgroundColor: colors[tier as keyof typeof colors] || '#718096' }}>Tier {tier}</span>;
}

export default function ValidationDashboard() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [expandedModel, setExpandedModel] = useState<string | null>(null);
  const [modelDetail, setModelDetail] = useState<ModelDetail | null>(null);
  const [pkgReady, setPkgReady] = useState<boolean | null>(null);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/v1/validation/summary`).then(r => r.json()).then(setSummary).catch(() => {});
    fetch(`${API}/api/v1/validation/package/Sym004-09`).then(r => r.json()).then(d => setPkgReady(d.submission_ready)).catch(() => {});
  }, []);

  const handleRowClick = (modelId: string) => {
    if (expandedModel === modelId) { setExpandedModel(null); setModelDetail(null); return; }
    setExpandedModel(modelId);
    const API = process.env.REACT_APP_API_URL || '';
    fetch(`${API}/api/v1/validation/model/${modelId}`).then(r => r.json()).then(setModelDetail).catch(() => {});
  };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>AI Model Validation — FDA/ICH Compliance</h1>
          <ComplianceBadges badges={['ICH E6(R3)', '21 CFR Part 11', 'ALCOA']} />
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24, marginTop: 16 }}>
          {[
            { label: 'Total Models', value: summary.total_models, color: '#e2e8f0' },
            { label: 'Compliant', value: summary.compliant, color: '#48bb78' },
            { label: 'Conditional', value: summary.conditional, color: '#ecc94b' },
            { label: 'Submission Ready', value: pkgReady ? 'Yes' : 'No', color: pkgReady ? '#48bb78' : '#f56565' },
          ].map((c) => (
            <div key={c.label} style={card}>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Validation matrix table */}
      {summary && (
        <div style={{ ...card, overflowX: 'auto', marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Validation Matrix</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, minWidth: 60 }}>ID</th>
                <th style={{ ...th, minWidth: 200 }}>Model</th>
                <th style={{ ...th, textAlign: 'center', minWidth: 40 }}>Tier</th>
                {LAYER_NAMES.map((l) => <th key={l} style={{ ...th, textAlign: 'center', minWidth: 45 }}>L{LAYER_NAMES.indexOf(l) + 1} {l}</th>)}
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.models.map((m) => (
                <React.Fragment key={m.model_id}>
                  <tr
                    onClick={() => handleRowClick(m.model_id)}
                    style={{ cursor: 'pointer', backgroundColor: expandedModel === m.model_id ? 'rgba(66,153,225,0.08)' : 'transparent' }}
                  >
                    <td style={{ ...td, fontWeight: 700, color: '#63b3ed' }}>{m.model_id}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{m.model_name}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{tierBadge(m.tier)}</td>
                    {m.layer_statuses.map((s, i) => (
                      <td key={i} style={{ ...td, textAlign: 'center' }}>{statusDot(s)}</td>
                    ))}
                    <td style={{ ...td, textAlign: 'center' }}>{statusBadge(m.overall_status)}</td>
                  </tr>
                  {expandedModel === m.model_id && modelDetail && (
                    <tr>
                      <td colSpan={LAYER_NAMES.length + 4} style={{ padding: 0, borderBottom: '2px solid #2d3748' }}>
                        <div style={{ backgroundColor: '#171923', padding: '16px 20px' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>
                            {modelDetail.model_name} — {modelDetail.context_of_use.business_purpose}
                          </div>
                          <div style={{ fontSize: 11, color: '#718096', marginBottom: 12 }}>
                            Hash: <code style={{ color: '#a0aec0' }}>{modelDetail.validation_hash.slice(0, 16)}...</code>
                            &nbsp;&middot;&nbsp;
                            Regulatory: {modelDetail.context_of_use.regulatory_basis.join(', ')}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            {modelDetail.layers.map((l) => (
                              <div key={l.layer_id} style={{ backgroundColor: '#1a202c', borderRadius: 6, padding: '8px 12px', border: `1px solid ${l.status === 'compliant' ? '#22543d' : l.status === 'partial' ? '#744210' : '#742a2a'}` }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: l.status === 'compliant' ? '#48bb78' : l.status === 'partial' ? '#ecc94b' : '#f56565', marginBottom: 4 }}>
                                  L{l.layer_id} {l.layer_name}
                                </div>
                                {l.evidence.slice(0, 2).map((e, i) => (
                                  <div key={i} style={{ fontSize: 10, color: '#a0aec0' }}>{'\u2713'} {e}</div>
                                ))}
                                {l.gaps.map((g, i) => (
                                  <div key={`g${i}`} style={{ fontSize: 10, color: '#f56565' }}>{'\u2717'} {g}</div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Compliance package section */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>Regulatory Submission Package</div>
          <button
            onClick={() => alert('Coming soon — validation package export')}
            style={{ padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#2b6cb0', color: '#fff' }}
          >
            Generate validation package PDF
          </button>
        </div>
        <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 12 }}>
          Package status: {pkgReady === null ? 'Loading...' : pkgReady ? <span style={{ color: '#48bb78', fontWeight: 600 }}>Ready for submission</span> : <span style={{ color: '#ecc94b', fontWeight: 600 }}>Conditional — gaps must be addressed</span>}
        </div>

        {/* Known gaps */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Model</th>
              <th style={th}>Gap</th>
              <th style={th}>Regulatory Req</th>
              <th style={th}>Priority</th>
            </tr>
          </thead>
          <tbody>
            {[
              { model: 'M1', gap: 'AUROC/FPR not formally computed', reg: 'FDA AI guidance L5', priority: 'Important' },
              { model: 'M1', gap: 'Independent test set not defined', reg: 'FDA AI guidance L5', priority: 'Important' },
              { model: 'M1', gap: 'No CTCAE drift detection', reg: 'ICH E6(R3) L8', priority: 'Advisory' },
              { model: 'M2', gap: 'No validation vs certified NCA software', reg: 'FDA PK guidance L5', priority: 'Critical' },
              { model: 'M6', gap: 'False positive rate not measured', reg: 'ICH E6(R3) L5', priority: 'Important' },
              { model: 'M8', gap: 'No precision/recall metrics', reg: 'FDA AI guidance L5', priority: 'Important' },
              { model: 'M9', gap: 'No back-testing vs historical SRC decisions', reg: 'FDA Adaptive Design L5', priority: 'Critical' },
              { model: 'M16', gap: 'No validation tests exist', reg: 'FDA Digital Health L5', priority: 'Critical' },
              { model: 'M17', gap: 'No validation tests exist', reg: 'FDA AI guidance L5', priority: 'Critical' },
              { model: 'M22', gap: 'No Bayesian prior sensitivity analysis', reg: 'FDA Adaptive Design L8', priority: 'Advisory' },
            ].map((g, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600, color: '#63b3ed' }}>{g.model}</td>
                <td style={{ ...td, fontSize: 12 }}>{g.gap}</td>
                <td style={{ ...td, fontSize: 11, color: '#a0aec0' }}>{g.reg}</td>
                <td style={td}>
                  <span style={{
                    padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    color: g.priority === 'Critical' ? '#f56565' : g.priority === 'Important' ? '#ecc94b' : '#63b3ed',
                    backgroundColor: g.priority === 'Critical' ? '#742a2a' : g.priority === 'Important' ? '#744210' : '#1a365d',
                  }}>{g.priority}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
